import { useMemo, useState } from 'react'
import {
  Eye,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Pencil,
  X,
} from 'lucide-react'
import { Button } from 'tailwind-v4/components/button'
import { Input } from 'tailwind-v4/components/input'
import { Label } from 'tailwind-v4/components/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'tailwind-v4/components/dialog'

interface ParsedAsset {
  url: string
  /** Visible link text (reflects renames). */
  label: string
  mime: string
}

const isImageMime = (mime: string) => (mime ?? '').startsWith('image/')

function iconFor(mime: string) {
  if (isImageMime(mime)) return ImageIcon
  if (mime === 'application/pdf') return FileText
  if (mime.includes('sheet') || mime.includes('excel') || mime === 'text/csv')
    return FileSpreadsheet
  if (mime.includes('word') || mime.includes('document')) return FileText
  return FileIcon
}

/** Pull asset links (the `data-asset` anchors inserted from the library) out of
 * the editor HTML so we can list them as viewable badges above the editor. */
function parseAssets(html: string): ParsedAsset[] {
  if (!html || typeof document === 'undefined') return []
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const anchors = Array.from(doc.querySelectorAll('a[data-asset]'))
  const seen = new Set<string>()
  const assets: ParsedAsset[] = []
  for (const a of anchors) {
    const url = a.getAttribute('href') ?? ''
    if (!url || seen.has(url)) continue
    seen.add(url)
    assets.push({
      url,
      label:
        a.textContent?.trim() || a.getAttribute('data-filename') || 'Asset',
      mime: a.getAttribute('data-mime') ?? '',
    })
  }
  return assets
}

interface AssetBadgeRowProps {
  html: string
  /** Remove the asset's link from the editor. */
  onRemove?: (url: string) => void
  /** Rename the asset link's visible text (href is preserved). */
  onRename?: (url: string, text: string) => void
}

/**
 * Row of attached-asset badges shown just under the editor toolbar. Derived
 * from the editor HTML, so it stays in sync as links are added/removed. Each
 * badge can preview (iframe/image), rename (display text only) or remove the
 * underlying link.
 */
export function AssetBadgeRow({
  html,
  onRemove,
  onRename,
}: AssetBadgeRowProps) {
  const assets = useMemo(() => parseAssets(html), [html])
  const [preview, setPreview] = useState<ParsedAsset | null>(null)
  const [renaming, setRenaming] = useState<ParsedAsset | null>(null)
  const [renameValue, setRenameValue] = useState('')

  if (assets.length === 0) return null

  const startRename = (asset: ParsedAsset) => {
    setRenameValue(asset.label)
    setRenaming(asset)
  }

  const commitRename = () => {
    if (renaming && renameValue.trim()) {
      onRename?.(renaming.url, renameValue.trim())
    }
    setRenaming(null)
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-black/10 px-2 py-1.5 dark:border-white/10">
      {assets.map((asset) => {
        const Icon = iconFor(asset.mime)
        return (
          <span
            key={asset.url}
            className="inline-flex max-w-[220px] items-center gap-1 rounded-full border border-black/10 bg-black/[0.03] py-0.5 pr-1 pl-2 text-xs font-medium text-[var(--foreground)] dark:border-white/10 dark:bg-white/[0.04]"
          >
            <Icon className="size-3 shrink-0 text-[var(--muted-foreground)]" />
            <span className="truncate">{asset.label}</span>
            <button
              type="button"
              aria-label={`Preview ${asset.label}`}
              title="Preview"
              onClick={() => {
                setPreview(asset)
              }}
              className="ml-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[var(--muted-foreground)] hover:bg-black/10 hover:text-[var(--foreground)] dark:hover:bg-white/15"
            >
              <Eye className="size-3" />
            </button>
            {onRename ? (
              <button
                type="button"
                aria-label={`Rename ${asset.label}`}
                title="Rename link text"
                onClick={() => {
                  startRename(asset)
                }}
                className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[var(--muted-foreground)] hover:bg-black/10 hover:text-[var(--foreground)] dark:hover:bg-white/15"
              >
                <Pencil className="size-3" />
              </button>
            ) : null}
            {onRemove ? (
              <button
                type="button"
                aria-label={`Remove ${asset.label}`}
                title="Remove"
                onClick={() => {
                  onRemove(asset.url)
                }}
                className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[var(--muted-foreground)] hover:bg-black/10 hover:text-[var(--foreground)] dark:hover:bg-white/15"
              >
                <X className="size-3" />
              </button>
            ) : null}
          </span>
        )
      })}

      {/* Preview dialog */}
      <Dialog
        open={preview !== null}
        onOpenChange={(open) => !open && setPreview(null)}
      >
        <DialogContent className="flex h-[80dvh] w-[min(900px,92vw)] !max-w-none flex-col gap-3">
          <DialogHeader>
            <DialogTitle
              title={preview?.label}
              className="block w-full truncate pr-8 text-base"
            >
              {preview?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-black/10 bg-black/[0.02] dark:border-white/10">
            {preview && isImageMime(preview.mime) ? (
              <div className="grid h-full place-items-center overflow-auto p-3">
                <img
                  src={preview.url}
                  alt={preview.label}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ) : preview ? (
              <iframe
                src={preview.url}
                title={preview.label}
                className="h-full w-full"
              />
            ) : null}
          </div>
          <div className="flex shrink-0 justify-end">
            {preview ? (
              <a href={preview.url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                  Open original
                </Button>
              </a>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog
        open={renaming !== null}
        onOpenChange={(open) => !open && setRenaming(null)}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename link</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="asset-rename">Link text</Label>
            <Input
              id="asset-rename"
              autoFocus
              value={renameValue}
              onChange={(e) => {
                setRenameValue(e.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  commitRename()
                }
              }}
            />
            <p className="text-xs text-[var(--muted-foreground)]">
              Only the text shown changes — the link still points to the same
              file.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setRenaming(null)
              }}
            >
              Cancel
            </Button>
            <Button variant="default" size="sm" onClick={commitRename}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AssetBadgeRow
